



import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiLogOut, FiAward, FiBook, FiUser, FiBarChart2 } from 'react-icons/fi';
import { frontendScenarios } from '../data/scenarios';

// Utility functions for points calculation
const calculateUserPoints = (completedScenarios) => {
  if (!completedScenarios || !Array.isArray(completedScenarios)) return 0;
  return completedScenarios.length * 20;
};

const getCompletedScenariosCount = (completedScenarios) => {
  if (!completedScenarios || !Array.isArray(completedScenarios)) return 0;
  return completedScenarios.length;
};

function UserDashboard() {
  const [user, setUser] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const validateTokenAndFetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Fetch user profile
        try {
          const userResponse = await axios.get('https://firstaidba.vercel.app/api/user/profile', {
            headers: { Authorization: `Bearer ${token}` },
          });
          setUser(userResponse.data);
        } catch (userError) {
          console.error('Error fetching user profile:', userError);
          // Create a mock user for frontend testing
          setUser({
            email: 'test@example.com',
            phone: '+1234567890',
            points: 0,
            completedScenarios: [],
            createdAt: new Date()
          });
        }

        // Use frontend scenarios instead of API call
        setScenarios(frontendScenarios);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        if (err.response?.status === 401) {
          navigate('/login');
        } else {
          setError('Failed to load dashboard data');
        }
      } finally {
        setLoading(false);
      }
    };

    validateTokenAndFetchData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/user-login');
  };

  const handleScenarioClick = (scenarioId) => {
    navigate(`/scenario/${scenarioId}`);
  };

  // Calculate points and completed scenarios count
  const completedScenariosCount = getCompletedScenariosCount(user?.completedScenarios);
  const totalPoints = calculateUserPoints(user?.completedScenarios);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-4 md:p-6">
      <header className="bg-white rounded-xl shadow-sm p-4 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-purple-800">First Aid Training</h1>
            <p className="text-gray-600 text-sm">Welcome back, {user?.email}</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-purple-100 text-purple-800 py-2 px-4 rounded-lg">
              <FiAward className="text-yellow-500" />
              <span className="font-semibold">{totalPoints} points</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 bg-red-100 hover:bg-red-200 text-red-700 py-2 px-4 rounded-lg transition-colors"
            >
              <FiLogOut /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiBook className="text-blue-600 text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Scenarios Completed</h3>
              <p className="text-2xl font-bold text-gray-900">{completedScenariosCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <FiAward className="text-green-600 text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Total Points</h3>
              <p className="text-2xl font-bold text-gray-900">{totalPoints}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-purple-100 rounded-lg">
              <FiUser className="text-purple-600 text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800">Account Created</h3>
              <p className="text-sm text-gray-600">
                {new Date(user?.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      <main className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-800">Training Scenarios</h2>
        </div>

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
            <p>{error}</p>
          </div>
        )}

        {scenarios.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <FiBook className="mx-auto text-gray-400 text-4xl mb-3" />
            <h3 className="text-lg font-medium text-gray-700">No scenarios available</h3>
            <p className="text-gray-500 mt-1">Check back later for new training scenarios</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scenarios.map((scenario) => {
              const isCompleted = user?.completedScenarios?.some(
                s => s.scenarioId === scenario._id || s.scenarioId === `frontend-${scenario._id}`
              );
              
              return (
                <div 
                  key={scenario._id} 
                  className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleScenarioClick(scenario._id)}
                >
                  <div className="h-40 bg-gradient-to-br from-purple-400 to-blue-400 flex items-center justify-center">
                    <div className="text-white text-center">
                      <FiBook className="text-4xl mx-auto mb-2" />
                      <h3 className="font-bold text-lg">{scenario.title}</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-lg text-gray-800 mb-2">{scenario.title}</h3>
                    <p className="text-gray-600 text-sm mb-4">{scenario.description}</p>
                    
                    <div className="flex justify-between items-center">
                      <span className="inline-flex items-center gap-1 text-sm font-medium text-purple-600">
                        <FiAward /> {scenario.points} points
                      </span>
                      
                      {isCompleted ? (
                        <span className="inline-flex items-center gap-1 text-sm font-medium text-green-600">
                          Completed ✓
                        </span>
                      ) : (
                        <span className="text-sm text-gray-500">Not started</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}

export default UserDashboard;